import React, { useState, useContext } from 'react';
import { RouteComponentProps } from '@reach/router';
import { Box, Grid, Stack, Avatar, Typography, Chip, Tooltip, IconButton } from '@mui/material';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { TextField, Button, Alert } from '@atoms';
import { ApplicationContext, callUpdateUser } from '@particles';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../../../firebase';

const NAVY = '#2b2258';
const GOLD = '#f0cf69';
const BRAND_GRADIENT = 'linear-gradient(135deg, #3182ce 0%, #887bdc 52%, #2b2258 100%)';

const cardSx = {
  backgroundColor: '#ffffff',
  border: '1px solid rgba(43, 34, 88, 0.08)',
  borderRadius: '12px',
  boxShadow: '0 1px 2px rgba(43, 34, 88, 0.04), 0 6px 16px rgba(43, 34, 88, 0.05)',
  p: 3
};

const inputSx = {
  position: 'relative',
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#F8F9FB',
    transition: 'background-color 220ms ease',
    '&:hover': { backgroundColor: '#F1F3F7' },
    '&.Mui-focused': { backgroundColor: '#ffffff' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(43, 34, 88, 0.18)',
      borderWidth: 1
    }
  },
  '& .MuiInputLabel-root.Mui-focused': { color: NAVY },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 12,
    right: 12,
    bottom: 0,
    height: 2,
    backgroundColor: GOLD,
    transform: 'scaleX(0)',
    transformOrigin: 'left center',
    transition: 'transform 240ms cubic-bezier(0.4, 0, 0.2, 1)',
    pointerEvents: 'none',
    borderRadius: 2
  },
  '&:focus-within::after': { transform: 'scaleX(1)' }
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (!parts.length || !parts[0]) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const MyProfile: React.FC<RouteComponentProps> = () => {
  const {
    state: { user },
    dispatch
  } = useContext(ApplicationContext);

  const [success, setSuccess] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [resetting, setResetting] = useState<boolean>(false);

  const [values, setValues] = useState({
    name: user?.name ?? '',
    email: user?.email ?? ''
  });

  const [errors, setErrors] = useState({
    name: '',
    email: ''
  });

  const handleChange = (prop: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, [prop]: event.target.value });
  };

  const validateFields = (): boolean => {
    let hasError = false;
    setErrors({ name: '', email: '' });
    if (values.name.trim().length === 0) {
      hasError = true;
      setErrors((state) => ({ ...state, name: 'Name is required' }));
    }
    return hasError;
  };

  const onSave = async () => {
    if (validateFields() || !user) return;

    setLoading(true);
    const { code, data, error } = await callUpdateUser(user.id, { name: values.name });

    if (code === 200 && data) {
      dispatch({ key: 'user', value: data, type: 'set' });
      localStorage.setItem('user', JSON.stringify(data));
      setSuccess('Profile updated successfully.');
      setTimeout(() => setSuccess(undefined), 3000);
    } else if (error) {
      setSuccess(error.message);
      setTimeout(() => setSuccess(undefined), 4000);
    }

    setLoading(false);
  };

  const handleResetPassword = () => {
    setResetting(true);
    sendPasswordResetEmail(auth, values.email)
      .then(() => {
        setSuccess('Password reset email sent. Please check your inbox.');
      })
      .catch((err) => {
        setSuccess(err.message);
      })
      .finally(() => {
        setResetting(false);
        setTimeout(() => setSuccess(undefined), 4000);
      });
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: 5 }, py: 4 }}>
      {success && (
        <Box sx={{ mb: 3 }}>
          <Alert message={success} />
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Left — identity card */}
        <Grid item xs={12} md={4}>
          <Box sx={{ ...cardSx, textAlign: 'center' }}>
            <Box sx={{ position: 'relative', display: 'inline-block', mb: 2.5 }}>
              <Avatar
                sx={{
                  width: 108,
                  height: 108,
                  fontSize: 40,
                  fontWeight: 700,
                  fontFamily: 'Montserrat',
                  background: 'rgba(240, 207, 105, 0.16)',
                  color: GOLD,
                  border: `2px solid ${GOLD}`
                }}
              >
                {getInitials(values.name) || <PersonOutlineOutlinedIcon sx={{ fontSize: 56 }} />}
              </Avatar>
              {/* Subtle brand-gradient outer glow */}
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  inset: -4,
                  borderRadius: '50%',
                  background: BRAND_GRADIENT,
                  opacity: 0.12,
                  zIndex: -1
                }}
              />
            </Box>

            <Typography
              sx={{
                fontFamily: 'Montserrat',
                fontSize: 18,
                fontWeight: 700,
                color: NAVY,
                mb: 0.5
              }}
            >
              {values.name || 'Your Name'}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Montserrat',
                fontSize: 13,
                color: '#6B7280',
                wordBreak: 'break-word',
                mb: 2
              }}
            >
              {values.email}
            </Typography>

            <Stack direction="row" spacing={1} justifyContent="center">
              <Chip
                label="Active"
                size="small"
                sx={{
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: 11,
                  backgroundColor: 'rgba(16, 185, 129, 0.12)',
                  color: '#059669',
                  border: '1px solid rgba(16, 185, 129, 0.25)'
                }}
              />
              {user?.role?.name && (
                <Chip
                  label={user.role.name}
                  size="small"
                  sx={{
                    fontFamily: 'Montserrat',
                    fontWeight: 600,
                    fontSize: 11,
                    backgroundColor: 'rgba(240, 207, 105, 0.16)',
                    color: '#8B6B12',
                    border: '1px solid rgba(240, 207, 105, 0.4)'
                  }}
                />
              )}
            </Stack>
          </Box>
        </Grid>

        {/* Right — editable sections */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Personal Information */}
            <Box sx={cardSx}>
              <Stack direction="row" alignItems="center" spacing={1.25} mb={2.5}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    background: 'rgba(43, 34, 88, 0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <PersonOutlineOutlinedIcon sx={{ color: NAVY, fontSize: 18 }} />
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'Montserrat',
                    fontSize: 16,
                    fontWeight: 700,
                    color: NAVY
                  }}
                >
                  Personal Information
                </Typography>
              </Stack>

              <Stack spacing={2.5}>
                <TextField
                  name="name"
                  label="Full Name"
                  fullWidth
                  onChange={handleChange('name')}
                  placeholder="Enter your full name"
                  errorMessage={errors.name}
                  value={values.name}
                  sx={inputSx}
                />
                <Box>
                  <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75}>
                    <Typography
                      sx={{
                        fontFamily: 'Montserrat',
                        fontSize: 13,
                        fontWeight: 600,
                        color: NAVY
                      }}
                    >
                      Email Address
                    </Typography>
                    <Tooltip
                      title="Email address is linked to your account and cannot be changed"
                      arrow
                      placement="top"
                    >
                      <IconButton size="small" sx={{ p: 0 }}>
                        <InfoIcon sx={{ fontSize: 15, color: '#9CA3AF' }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <TextField
                    name="email"
                    label=""
                    type="email"
                    fullWidth
                    onChange={handleChange('email')}
                    placeholder="name@company.com"
                    disabled
                    errorMessage={errors.email}
                    value={values.email}
                    sx={inputSx}
                  />
                </Box>
              </Stack>

              <Box
                sx={{
                  mt: 3,
                  pt: 2.5,
                  borderTop: '1px solid rgba(43, 34, 88, 0.08)',
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}
              >
                <Button
                  onClick={onSave}
                  loading={loading}
                  startIcon={<SaveIcon />}
                  sx={{
                    minWidth: 140,
                    borderRadius: '10px',
                    fontWeight: 600,
                    background: BRAND_GRADIENT,
                    color: '#ffffff',
                    boxShadow: '0 4px 12px rgba(43, 34, 88, 0.18)',
                    '&:hover': {
                      background: BRAND_GRADIENT,
                      filter: 'brightness(1.05)',
                      boxShadow: '0 6px 16px rgba(43, 34, 88, 0.24)'
                    }
                  }}
                >
                  Save Changes
                </Button>
              </Box>
            </Box>

            {/* Security */}
            <Box sx={cardSx}>
              <Stack direction="row" alignItems="center" spacing={1.25} mb={2.5}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    background: 'rgba(43, 34, 88, 0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <LockOutlinedIcon sx={{ color: NAVY, fontSize: 18 }} />
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'Montserrat',
                    fontSize: 16,
                    fontWeight: 700,
                    color: NAVY
                  }}
                >
                  Security
                </Typography>
              </Stack>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  p: 2,
                  bgcolor: '#F8F9FB',
                  borderRadius: '10px',
                  border: '1px solid rgba(43, 34, 88, 0.06)'
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat',
                      fontSize: 14,
                      fontWeight: 600,
                      color: NAVY,
                      mb: 0.25
                    }}
                  >
                    Password
                  </Typography>
                  <Typography
                    sx={{ fontFamily: 'Montserrat', fontSize: 12, color: '#6B7280' }}
                  >
                    Send a password reset link to your email
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  onClick={handleResetPassword}
                  loading={resetting}
                  sx={{
                    minWidth: 120,
                    borderRadius: '10px',
                    borderColor: 'rgba(43, 34, 88, 0.25)',
                    color: NAVY,
                    '&:hover': {
                      borderColor: NAVY,
                      backgroundColor: 'rgba(43, 34, 88, 0.04)'
                    }
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MyProfile;
